import { supabase } from '@/shared/lib/supabase';
import {
  CALENDAR_MINE_STATUSES,
  CALENDAR_STATUSES,
  PENDING_STATUSES,
  isProjectStatus,
} from '@/shared/constants/statuses';
import type {
  AgendaProject,
  DateNeighborhoodItem,
  Id,
  MinistryMember,
  ProjectCatalogs,
  ProjectContact,
  ProjectDecision,
  ProjectFilters,
  ProjectSupportItem,
  ProjectTimelineEntry,
  ProjectTimelineRow,
  ProjectWithContext,
  SpaceConflict,
  SpaceConflictPreview,
  WritableMinistry,
} from '@/shared/types/domain';

/**
 * Única camada da feature `projects` que conhece o supabase-js
 * (architecture.md §5.1).
 *
 * **Toda escrita é `rpc(...)`, nunca `.insert()`/`.update()`** — e isso não é
 * estilo: `projects` não tem política de escrita para papel nenhum (spec
 * §5.2), então não existe caminho pelo PostgREST para escrever. A RPC
 * `SECURITY DEFINER` é o único, e é ela que checa escopo, versão e transição.
 */

/** O que a view devolve, antes da estreitagem. */
type ProjectRow = {
  id: string | null;
  ministry_id: string | null;
  ministry_name: string | null;
  coordination_id: string | null;
  coordination_name: string | null;
  branch_id: string | null;
  title: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  starts_at: string | null;
  ends_at: string | null;
  expected_audience: number | null;
  responsible_profile_id: string | null;
  responsible_name: string | null;
  estimated_budget_cents: number | null;
  funding_source_id: string | null;
  funding_source_name: string | null;
  budget_notes: string | null;
  extra_info: string | null;
  status: string | null;
  version: number | null;
  created_by: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  space_names: string[] | null;
};

/**
 * Estreita uma linha da view para o tipo de domínio.
 *
 * O Postgres não consegue provar que coluna de view é não-nula, então o
 * gerador marca TODAS como anuláveis — inclusive `id` e `status`. Se esse tipo
 * vazasse para a UI, cada tela carregaria `?.` e `?? ''` em campos que nunca
 * são nulos, e o `title: string | null` de verdade (rascunho nasce vazio,
 * ADR-005) se perderia no meio do ruído.
 *
 * Linha que não tiver a espinha dorsal — identidade, cadeia organizacional,
 * situação — é DESCARTADA, não remendada: é sinal de a view ter mudado de
 * forma, e uma tela a menos é mais fácil de diagnosticar que um `undefined`
 * atravessando três componentes.
 */
function narrow(row: ProjectRow): ProjectWithContext | null {
  const {
    id,
    ministry_id,
    ministry_name,
    coordination_id,
    coordination_name,
    branch_id,
    status,
    created_by,
    created_at,
    updated_at,
    version,
  } = row;

  if (
    id === null ||
    ministry_id === null ||
    ministry_name === null ||
    coordination_id === null ||
    coordination_name === null ||
    branch_id === null ||
    status === null ||
    !isProjectStatus(status) ||
    created_by === null ||
    created_at === null ||
    updated_at === null ||
    version === null
  ) {
    return null;
  }

  return {
    id,
    ministry_id,
    ministry_name,
    coordination_id,
    coordination_name,
    branch_id,
    title: row.title,
    description: row.description,
    category_id: row.category_id,
    category_name: row.category_name,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    expected_audience: row.expected_audience,
    responsible_profile_id: row.responsible_profile_id,
    responsible_name: row.responsible_name,
    estimated_budget_cents: row.estimated_budget_cents,
    funding_source_id: row.funding_source_id,
    funding_source_name: row.funding_source_name,
    budget_notes: row.budget_notes,
    extra_info: row.extra_info,
    status,
    version,
    created_by,
    submitted_at: row.submitted_at,
    decided_at: row.decided_at,
    closed_at: row.closed_at,
    created_at,
    updated_at,
    space_names: row.space_names ?? [],
  };
}

/**
 * Projetos visíveis para a pessoa, opcionalmente recortados.
 *
 * Os filtros são conveniência de leitura: a RLS já entregou só o que ela pode
 * ver, e tirar um filtro nunca revela uma linha nova. Ordem por data de início
 * crescente, com quem ainda não tem data (rascunho) primeiro — é o que precisa
 * de ação de quem o vê.
 *
 * `excludeDrafts` é o recorte do acervo de `/projetos`, e mora aqui em vez de
 * na tela para que a contagem exibida conte exatamente as linhas exibidas.
 */
export async function fetchProjects(
  filters: ProjectFilters = {},
): Promise<ProjectWithContext[]> {
  let query = supabase.from('projects_with_context').select('*');

  if (filters.coordinationId) query = query.eq('coordination_id', filters.coordinationId);
  if (filters.ministryId) query = query.eq('ministry_id', filters.ministryId);
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.excludeDrafts) query = query.neq('status', 'draft');
  if (filters.search?.trim()) {
    // `%` e `,` quebram o parser de filtro do PostgREST; escapar aqui evita
    // que um título com vírgula vire dois filtros.
    const term = filters.search.trim().replace(/[%,]/g, ' ');
    query = query.ilike('title', `%${term}%`);
  }

  const { data, error } = await query.order('starts_at', {
    ascending: true,
    nullsFirst: true,
  });
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    const project = narrow(row);
    return project ? [project] : [];
  });
}

export interface ProjectFilterOptions {
  coordinations: { id: Id; name: string }[];
  ministries: { id: Id; name: string; coordination_id: Id }[];
}

/**
 * Opções dos seletores de filtro.
 *
 * Lê `coordinations` e `ministries` daqui, e não do barrel de `features/org`,
 * para manter a dependência entre features em uma direção só: `org` consome a
 * listagem de projetos, e `projects` não consome nada de `org` (ADR-006 — o
 * ciclo entre dois barrels é o começo da bola de lama). São duas consultas de
 * poucas dezenas de linhas; a alternativa seria montar as opções varrendo os
 * projetos carregados, que o `max_rows = 1000` do PostgREST truncaria em
 * silêncio no dia em que a igreja passasse disso.
 *
 * Cada uma vem recortada pela RLS: o líder recebe os ministérios da própria
 * coordenação, o coordenador os da dele, e o overseer os da filial.
 */
export async function fetchProjectFilterOptions(): Promise<ProjectFilterOptions> {
  const [coordinationsRes, ministriesRes] = await Promise.all([
    supabase.from('coordinations').select('id, name').order('name'),
    supabase.from('ministries').select('id, name, coordination_id').order('name'),
  ]);
  if (coordinationsRes.error) throw coordinationsRes.error;
  if (ministriesRes.error) throw ministriesRes.error;

  return {
    coordinations: coordinationsRes.data ?? [],
    ministries: ministriesRes.data ?? [],
  };
}

export async function fetchProject(id: Id): Promise<ProjectWithContext | null> {
  const { data, error } = await supabase
    .from('projects_with_context')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ? narrow(data) : null;
}

/**
 * "O que está parado esperando VOCÊ" (US-16, US-21 · decisão 49): rascunhos e
 * projetos devolvidos para ajuste, ordenados pelo que foi mexido por último.
 *
 * ⚠️ **O escopo precisa ser explícito, e a RLS não o dá de graça.** Isto era
 * `fetchDrafts`, sem filtro de escopo nenhum, e a lista era pessoal **por
 * acidente**: a política estreita apenas o `draft`.
 *
 * ```sql
 * and ministry_id in (select my_ministry_ids())        -- cascata: filial inteira
 * and (
 *   status <> 'draft'                                  -- ⚠️ changes_requested cai AQUI
 *   or ministry_id in (select my_draft_ministry_ids()) -- vínculo direto (+ admin)
 *   or created_by = current_profile_id()
 * )
 * ```
 *
 * Acrescentar `changes_requested` sem mais nada devolveria, para um overseer,
 * **todos os projetos devolvidos da filial inteira** — e para um coordenador os
 * da coordenação, estes com um botão "Ajustar" que a RPC recusa. "Minhas
 * pendências" viraria "pendências de todo mundo", com duas populações de
 * escopos diferentes dentro da mesma lista.
 *
 * `my_draft_ministry_ids()` é exatamente a semântica que falta — só vínculo
 * **direto** de ministério, mais admin —, e não precisa de migration: ela já é
 * chamável por `authenticated`, como `my_ministry_ids()` já é hoje. O filtro é
 * redundante para o `draft`, e redundância na direção restritiva é barata.
 *
 * **Para overseer e coordenador sem vínculo de ministério a tela fica vazia**, e
 * isso é correto: a pendência de quem analisa mora em `/analise`, e a de quem
 * acompanha, no painel da coordenação.
 */
export async function fetchPendingProjects(): Promise<ProjectWithContext[]> {
  const mine = await supabase.rpc('my_draft_ministry_ids');
  if (mine.error) throw mine.error;

  const ministryIds = mine.data ?? [];
  // Sem vínculo direto não há pendência pessoal — e um `.in()` com lista vazia
  // é uma ida ao servidor cuja resposta já se sabe.
  if (ministryIds.length === 0) return [];

  const { data, error } = await supabase
    .from('projects_with_context')
    .select('*')
    .in('status', PENDING_STATUSES)
    .in('ministry_id', ministryIds)
    .order('updated_at', { ascending: false });
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    const project = narrow(row);
    return project ? [project] : [];
  });
}

/** Demandas dirigidas a outros ministérios (ADR-010). */
export async function fetchProjectSupportItems(id: Id): Promise<ProjectSupportItem[]> {
  const { data, error } = await supabase
    .from('project_support_items')
    .select('id, request, needed_by, support_types!inner(name, group_name, position)')
    .eq('project_id', id)
    .order('position', { referencedTable: 'support_types' });
  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    support_type_name: row.support_types.name,
    group_name: row.support_types.group_name,
    request: row.request,
    needed_by: row.needed_by,
  }));
}

// ── Vocabulário do formulário ────────────────────────────────────────────────

/**
 * Os quatro catálogos numa consulta só (spec §4.1).
 *
 * `is_active = false` some da lista, mas a linha continua existindo para o
 * projeto histórico que a referencia — é o soft-delete de catálogo, e é por
 * isso que o filtro vive aqui e não numa migration de exclusão.
 */
export async function fetchProjectCatalogs(): Promise<ProjectCatalogs> {
  const [spaces, categories, supportTypes, fundingSources] = await Promise.all([
    supabase
      .from('spaces')
      .select('id, name, capacity')
      .eq('is_active', true)
      .order('position')
      .order('name'),
    supabase
      .from('project_categories')
      .select('id, name')
      .eq('is_active', true)
      .order('position')
      .order('name'),
    supabase
      .from('support_types')
      .select('id, name, group_name, request_hint')
      .eq('is_active', true)
      .order('position')
      .order('name'),
    supabase
      .from('funding_sources')
      .select('id, name')
      .eq('is_active', true)
      .order('position')
      .order('name'),
  ]);

  for (const result of [spaces, categories, supportTypes, fundingSources]) {
    if (result.error) throw result.error;
  }

  return {
    spaces: spaces.data ?? [],
    categories: categories.data ?? [],
    supportTypes: supportTypes.data ?? [],
    fundingSources: fundingSources.data ?? [],
  };
}

/**
 * Ministérios onde a pessoa pode criar projeto (US-08).
 *
 * RPC, e não `from('ministries')`: a política de leitura de ministérios é mais
 * larga que o direito de escrita — um líder enxerga os ministérios irmãos da
 * coordenação dele. Filtrar no cliente exigiria reimplementar
 * `my_writable_ministry_ids()` aqui, e autorização derivada em dois lugares é
 * autorização que diverge.
 */
export async function fetchWritableMinistries(): Promise<WritableMinistry[]> {
  const { data, error } = await supabase.rpc('list_writable_ministries');
  if (error) throw error;
  return data ?? [];
}

/** Quem pode ser responsável: vínculo vigente no ministério, e só (§5.4). */
export async function fetchMinistryMembers(ministryId: Id): Promise<MinistryMember[]> {
  const { data, error } = await supabase.rpc('list_ministry_members', {
    p_ministry_id: ministryId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Alerta de conflito enquanto o líder escolhe data e espaço (decisão 15).
 *
 * A RPC devolve `label` nulo quando a ocupação é de projeto de outro
 * ministério: saber QUE está ocupado evita a rodada de ajustes mais óbvia que
 * existe; saber de quem furaria a RLS.
 */
export async function fetchSpaceConflictPreview(
  spaceIds: readonly Id[],
  startsAt: string,
  endsAt: string,
  excludeProjectId?: Id,
): Promise<SpaceConflictPreview[]> {
  const { data, error } = await supabase.rpc('preview_space_conflicts', {
    p_space_ids: [...spaceIds],
    p_starts_at: startsAt,
    p_ends_at: endsAt,
    p_exclude_project_id: excludeProjectId ?? undefined,
  });
  if (error) throw error;
  return data ?? [];
}

// ── Escrita: RPC, sempre ─────────────────────────────────────────────────────

/** O que o formulário manda para `save_project_draft` (§6). */
export interface ProjectDraftPayload {
  ministry_id?: Id;
  title?: string | null;
  description?: string | null;
  category_id?: Id | null;
  starts_at?: string | null;
  ends_at?: string | null;
  expected_audience?: number | null;
  responsible_profile_id?: Id | null;
  estimated_budget_cents?: number | null;
  funding_source_id?: Id | null;
  budget_notes?: string | null;
  extra_info?: string | null;
  space_ids?: Id[];
  support_items?: { support_type_id: Id; request?: string | null; needed_by?: string | null }[];
}

export interface SavedProject {
  id: Id;
  version: number;
  status: string;
}

/**
 * Cria (quando `projectId` é nulo) ou atualiza o rascunho no servidor.
 *
 * **A chave presente no payload é o que decide o que gravar** — mandar
 * `{ title: null }` limpa o título; não mandar `title` não o toca. É por isso
 * que quem monta o payload precisa mandar só o que mudou, e nunca o formulário
 * inteiro com `undefined` espalhado.
 *
 * `expectedVersion` é a concorrência otimista do ADR-005: vem sempre da última
 * resposta do servidor, e um conflito vira `draft_version_conflict` — nunca
 * sobrescrita silenciosa.
 */
export async function saveProjectDraft(
  projectId: Id | null,
  payload: ProjectDraftPayload,
  expectedVersion: number | null,
): Promise<SavedProject> {
  const { data, error } = await supabase.rpc('save_project_draft', {
    p_project_id: projectId ?? undefined,
    p_payload: payload as never,
    p_expected_version: expectedVersion ?? undefined,
  });
  if (error) throw error;

  const row = data as { id: string; version: number; status: string } | null;
  if (!row) throw new Error('save_project_draft não devolveu o projeto salvo.');
  return { id: row.id, version: row.version, status: row.status };
}

/** Envia para análise. `comment` é a observação de reenvio (decisão 16). */
export async function submitProject(projectId: Id, comment?: string | null): Promise<void> {
  const { error } = await supabase.rpc('submit_project', {
    p_project_id: projectId,
    p_comment: comment?.trim() ? comment.trim() : undefined,
  });
  if (error) throw error;
}

/** Soft delete, só em rascunho. Não apaga: alimenta a métrica de abandono. */
export async function discardDraft(projectId: Id): Promise<void> {
  const { error } = await supabase.rpc('discard_draft', { p_project_id: projectId });
  if (error) throw error;
}

// ── Caminho de decisão (spec-project-review.md §4) ──────────────────────────

/* `startReview` e `releaseReview` moravam aqui e SAÍRAM com as RPCs que elas
   chamavam (ADR-013): a reivindicação de análise deixou de existir na esteira, e
   as duas funções não existem mais no banco. Não recrie — o botão "Iniciar
   análise" do painel de decisão é `useState` puro e não deve chamar RPC
   nenhuma. */

/**
 * Aprovar, solicitar ajustes ou rejeitar — os três verbos da análise (§4.3).
 * `comment` opcional em `approve`; obrigatório nos outros dois, checado pela
 * RPC (que consulta `project_transitions` em vez de repetir a regra aqui).
 */
export async function decideProject(
  projectId: Id,
  decision: ProjectDecision,
  comment?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('decide_project', {
    p_project_id: projectId,
    p_decision: decision,
    p_comment: comment?.trim() ? comment.trim() : undefined,
  });
  if (error) throw error;
}

/** O evento não vai acontecer. Comentário sempre obrigatório (§4.4). */
export async function cancelProject(projectId: Id, comment: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_project', {
    p_project_id: projectId,
    p_comment: comment,
  });
  if (error) throw error;
}

/**
 * `approved → completed`, por quem escreve no ministério (§4.5).
 *
 * ⚠️ **Sem chamador desde a decisão 29**: "Marcar como concluído" saiu da tela
 * — ninguém declara que um evento aconteceu, a data declara. Fica aqui, como
 * `startReview` e `releaseReview`, esperando o relatório pós-projeto da v1.1.
 */
export async function completeProject(projectId: Id): Promise<void> {
  const { error } = await supabase.rpc('complete_project', { p_project_id: projectId });
  if (error) throw error;
}

/**
 * O contato do responsável, para quem decide (decisão 30, §8).
 *
 * RPC, e não `select` em `profiles`: a política de perfis é `me OR admin` por
 * construção, e abrir um ramo nela liberaria a linha inteira. Zero linhas =
 * projeto sem responsável; `membership_active = false` = vínculo encerrado, e
 * aí e-mail e telefone vêm nulos do próprio banco (RNF-S09).
 */
export async function fetchProjectContact(projectId: Id): Promise<ProjectContact | null> {
  const { data, error } = await supabase.rpc('get_project_contact', { p_project_id: projectId });
  if (error) throw error;
  return data?.[0] ?? null;
}

/**
 * O alerta que o REVISOR vê (US-20, §4.6) — detalhe completo, ao contrário de
 * `fetchSpaceConflictPreview` (do líder, `label` sempre nulo). A guarda de
 * autorização (`can_decide_for_ministry`) vive na própria RPC — §5.1.
 */
export async function fetchSpaceConflicts(projectId: Id): Promise<SpaceConflict[]> {
  const { data, error } = await supabase.rpc('get_space_conflicts', { p_project_id: projectId });
  if (error) throw error;
  return data ?? [];
}

/**
 * Estreita uma linha de `project_timeline` (view — toda coluna `| null`).
 * `kind` decide qual dos dois grupos de campos é válido; uma linha que não
 * bater em nenhum dos dois é descartada, mesma razão de `narrow()` acima.
 */
function narrowTimelineEntry(row: ProjectTimelineRow): ProjectTimelineEntry | null {
  if (row.project_id === null || row.created_at === null || row.actor_name === null) return null;

  const base = {
    project_id: row.project_id,
    created_at: row.created_at,
    actor_profile_id: row.actor_profile_id,
    actor_name: row.actor_name,
  };

  if (row.kind === 'transition') {
    if (row.to_status === null || !isProjectStatus(row.to_status)) return null;
    return {
      ...base,
      kind: 'transition',
      from_status: row.from_status && isProjectStatus(row.from_status) ? row.from_status : null,
      to_status: row.to_status,
      comment: row.comment,
    };
  }

  if (row.kind === 'field_change') {
    if (row.field === null) return null;
    return {
      ...base,
      kind: 'field_change',
      field: row.field,
      old_value: row.old_value,
      new_value: row.new_value,
    };
  }

  return null;
}

/**
 * Transição e mudança de campo, unidas numa consulta só (US-24, §4.7). A RLS
 * das duas tabelas de origem se aplica sozinha (`security_invoker`) — nenhum
 * filtro de escopo aqui.
 */
export async function fetchProjectTimeline(projectId: Id): Promise<ProjectTimelineEntry[]> {
  const { data, error } = await supabase
    .from('project_timeline')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    const entry = narrowTimelineEntry(row as ProjectTimelineRow);
    return entry ? [entry] : [];
  });
}

/**
 * Quantos dias a vizinhança cobre para cada lado da janela do projeto.
 *
 * **Exportada porque a TELA precisa dizer o intervalo** (decisão 38): o título
 * "Nesta semana" descrevia sete dias enquanto a consulta cobria vinte e oito, e
 * quem lia concluía que a igreja tinha menos coisa marcada do que tem. O número
 * não pode viver em dois lugares — mudar aqui muda o texto exibido, sem editar
 * copy nenhuma.
 */
export const NEIGHBORHOOD_WINDOW_DAYS = 14;

/**
 * Vizinhança da data (§6.5): ±`NEIGHBORHOOD_WINDOW_DAYS` em torno da janela do
 * projeto, nos `CALENDAR_STATUSES`. Sem RPC — `projects_with_context` já
 * recorta pela RLS; o filtro de filial existe porque, para `admin`,
 * `my_ministry_ids()` alcança MAIS de uma filial, e a faixa é sobre "o que mais
 * está marcado nesta igreja perto desta data".
 */
export async function fetchDateNeighborhood(
  branchId: Id,
  startsAt: string,
  endsAt: string,
): Promise<DateNeighborhoodItem[]> {
  const offset = NEIGHBORHOOD_WINDOW_DAYS * 86_400_000;
  const windowStart = new Date(new Date(startsAt).getTime() - offset).toISOString();
  const windowEnd = new Date(new Date(endsAt).getTime() + offset).toISOString();

  const { data, error } = await supabase
    .from('projects_with_context')
    .select('id, title, ministry_name, starts_at, ends_at, status')
    .eq('branch_id', branchId)
    .in('status', CALENDAR_STATUSES)
    .not('starts_at', 'is', null)
    .gte('starts_at', windowStart)
    .lte('starts_at', windowEnd)
    .order('starts_at', { ascending: true });
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    if (
      row.id === null ||
      row.ministry_name === null ||
      row.starts_at === null ||
      row.ends_at === null ||
      row.status === null ||
      !isProjectStatus(row.status)
    ) {
      return [];
    }
    return [
      {
        id: row.id,
        title: row.title,
        ministry_name: row.ministry_name,
        starts_at: row.starts_at,
        ends_at: row.ends_at,
        status: row.status,
      },
    ];
  });
}

// ── A agenda da igreja (spec-calendar.md §3.3) ───────────────────────────────

/**
 * A projeção reduzida de `agenda_projects` — **a única consulta do painel que
 * atravessa a fronteira de ministério**, e a razão de a aba de Calendário
 * existir para 145 das 150 pessoas.
 *
 * ⚠️ **Mora aqui, e não em `features/calendar/api/`, para evitar um ciclo**
 * (spec §10). `features/calendar` importa `ProjectTable` de `features/projects`;
 * se a leitura reduzida nascesse do lado do calendário, `ProjectDetailPage`
 * teria de importá-la de volta e as duas features passariam a depender uma da
 * outra. Além de resolver a dependência, é o lugar certo por conteúdo: quem
 * consome a leitura reduzida é a **tela de detalhe do projeto**.
 *
 * **Uma função para os dois usos** — o intervalo do calendário e o projeto
 * avulso de §7.2 — porque a lista de colunas não pode existir em dois lugares:
 * duas funções divergem, e no dia em que divergirem uma delas estará expondo
 * mais que a outra sem ninguém perceber.
 *
 * O recorte de situação é do SERVIDOR: `under_review` só sai para quem tem
 * papel de decisão, e nenhum argumento daqui participa disso.
 */
export async function fetchAgendaProjects(params: {
  from?: string;
  to?: string;
  projectId?: Id;
}): Promise<AgendaProject[]> {
  const { data, error } = await supabase.rpc('agenda_projects', {
    p_from: params.from ?? undefined,
    p_to: params.to ?? undefined,
    p_project_id: params.projectId ?? undefined,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * A metade do calendário que **abre inteiro** (spec-calendar-ux.md §3.2).
 *
 * `select` normal, com a RLS de sempre — **não** passa pela RPC, e é isso que a
 * torna completa: a pessoa recebe a linha inteira, com todas as colunas que ela
 * já tinha direito de ler. Consequência direta na tela: o que vem por aqui é
 * chip **preenchido** e abre o projeto em detalhe completo, nunca a folha de
 * quatro campos.
 *
 * ⚠️ **Chamava-se `fetchMyCalendarProjects`, e o nome mentia depois da
 * [ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md).** Ela nunca
 * devolveu "meus" projetos: devolve o ALCANCE DE LEITURA, que para um
 * coordenador é a coordenação inteira e para um admin é a igreja. Enquanto
 * existia a fonte "Meus Projetos", o calendário estreitava esse conjunto no
 * cliente e o nome passava; sem a fonte, o que sobra é exatamente o que a RLS
 * dá — e "meu" virou peso visual, calculado em
 * `features/calendar/lib/items.ts`.
 *
 * `CALENDAR_MINE_STATUSES` continua sendo o recorte: são os quatro status vivos
 * (o mesmo conjunto da RPC), e é ele que mantém o rascunho fora da grade mesmo
 * quando a RLS o entrega.
 *
 * A janela é por SOBREPOSIÇÃO, como a da RPC (decisão 53): um retiro de 30/01 a
 * 02/02 tem de aparecer nos dois meses.
 */
export async function fetchReadableCalendarProjects(
  from: string,
  to: string,
): Promise<ProjectWithContext[]> {
  const { data, error } = await supabase
    .from('projects_with_context')
    .select('*')
    .in('status', CALENDAR_MINE_STATUSES)
    .not('starts_at', 'is', null)
    .not('ends_at', 'is', null)
    .lt('starts_at', to)
    .gte('ends_at', from)
    .order('starts_at', { ascending: true });
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    const project = narrow(row);
    return project ? [project] : [];
  });
}
